/**
 * App shell: UI = f(store state). One subscription at the top; screens are
 * pure projections receiving state + action callbacks. The rail wraps every
 * screen except Login (§4.1). Scroll reset + focus-to-title fire on every
 * navSeq change (§8) — the store is the single source of truth; the DOM
 * follows it, never the reverse.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Store } from './store/store.js';
import {
  navigate,
  goBack,
  goHome,
  startJob,
  patchJob,
  resetForNewQuote,
  openBookingDetail,
  openHome,
  setHomeDetails,
  openNursingFromRail,
  furthestReadyScreen,
  type AppState,
  type ScreenId,
} from './store/state.js';
import { bands, channelLabel, isChannelConfigured, serviceById, serviceLabel, addOns, sendStatusLabel, type Registry } from './store/registry.js';
import {
  doLogin,
  doLogout,
  INBOX_POLL_MS,
  refreshInboxCount,
  refreshPortalCount,
  fetchQuote,
  persistCurrentQuote,
  parkCurrentQuote,
  refreshPayment,
  loadInbox,
  quoteThisInquiry,
  setInquiryStatus,
  refreshBookingDetail,
  sendQuoteToCustomer,
  previewTeamMessage,
  copyQuoteMessage,
  copyPaymentLink,
  COPY_FAILED_COPY,
  COPY_LINK_FAILED_COPY,
  COPY_REPRICING_COPY,
  sendTeamAlert,
  reopenBooking,
  refreshRateChangesCount,
  setBand,
  setOption,
  setPassengersExact,
  commitPassengersExact,
  setVenueAndQuote,
  setPickup,
  setDropoff,
  setOperatorStop,
  setGolfEnd,
  airportEndOf,
  setAirportEnd,
  setPickupOffGazetteer,
  setDropoffOffGazetteer,
  reportLocationMiss,
  loadPrecedent,
  type PrecedentEntry,
} from './lib/actions.js';
import { syncOnScreenOpen } from './lib/push-register.js';
import { journeyExtras, journeyLineWithoutExtras, journeySummary, quotePending, precedentPlace } from './store/selectors.js';
import { Rail } from './components/Rail.js';
import { FeedbackHost } from './feedback/host.js';
import { LeadContext } from './components/LeadContext.js';
import { Login } from './screens/Login.js';
import { Home } from './screens/Home.js';
import { LocationScreen } from './screens/Location.js';
import { Passengers } from './screens/Passengers.js';
import { Addons } from './screens/Addons.js';
import { QuoteScreen } from './screens/Quote.js';
import { Done } from './screens/Done.js';
import { Inbox } from './screens/Inbox.js';
import { BACK_TO_BOOKINGS, Bookings, type BookingsLanding, type BookingsWhen } from './screens/Bookings.js';
import { BookingDetail } from './screens/BookingDetail.js';
import { Admin } from './screens/Admin.js';
/* ⛔ THIS SPECIFIER IS THE KILLSWITCH'S HOOK AND MUST STAY EXACTLY `./screens/
   Catalog.js`. `scripts/build-client.mjs` matches it with an esbuild `onResolve`
   filter and swaps in `src/client/catalog-off.tsx` unless `ECMB_CATALOG` is `1`;
   rewrite it to a relative variant the filter does not match and the specimens
   ship to Helen with CI fully green. The same rule the feedback host carries. */
import { Catalog, CATALOG_PRESENT } from './screens/Catalog.js';
import { RateChanges } from './screens/RateChanges.js';
import { Triage } from './screens/Triage.js';
import { Operators } from './screens/admin/Operators.js';
import { NursingHomes, type NursingTab } from './screens/admin/NursingHomes.js';

/** The wizard steps Helen quotes THROUGH. Quote is excluded on purpose: it
 *  already carries CustomerInfoBlock, the full read of the same lead. */
const LEAD_CONTEXT_SCREENS = new Set<ScreenId>(['location', 'passengers', 'addons']);

export function App({ store }: { store: Store }) {
  const [state, setState] = useState<AppState>(store.getState());
  const [registry, setRegistry] = useState<Registry | null>(null);
  const [precedent, setPrecedent] = useState<PrecedentEntry[]>([]);
  /**
   * ⭐⭐ T1749 M7 (R5) — WHICH TAB `NursingHomes` SHOULD OPEN ON, when something
   * else on the tool has asked. The shelf's `Customer accounts & prices` entry
   * navigates to the Nursing homes screen and lands on its Homes tab, where the
   * account book now lives.
   *
   * ⛔ NOT A NEW `ScreenId`, WHICH IS THE RULING (R5, C7). The account book is a
   * SECTION of an existing destination, not a destination of its own, and minting
   * a ScreenId for it would put it back in the rail's budget (§4.3's height plane)
   * and give one thing two ways in.
   * ⛔ AND NOT IN THE STORE EITHER. `navigate` moves between SCREENS; which tab a
   * screen opens on is that screen's own affair and survives exactly one
   * navigation, so a store field would be a durable record of a momentary
   * request. This is App holding a hand-off for the render it is about to do.
   * ⚠️ CLEARED BY THE SCREEN, not by a timer: `NursingHomes` reads it once at
   * mount (its bootstrap is computed once by design) and calls `onOpened`, so a
   * later visit to the same screen opens on the ordinary bootstrap answer.
   */
  // ⭐ board 2812 M4: `setup` (Set up homes and places) replaces the Homes tab here.
  const [nursingOpenOn, setNursingOpenOn] = useState<'setup' | 'todo' | undefined>(undefined);
  /**
   * ⭐ board 2812 M3 fix round (Captain's item 5) - BACK FROM A BOOKING OPENED ON
   * NURSING HOMES > BOOKINGS RETURNS TO THAT TAB, not to the screen's opening tab.
   * Set when the Bookings tab opens a booking; read as `openOn` when Nursing homes
   * mounts again (Back pops to it) and consumed there (`onOpened`). ⛔ It survives the
   * booking's own screen ONLY: any other screen clears it (the effect below), so a
   * later visit by the rail opens on To do as usual.
   */
  const [nursingReturnTo, setNursingReturnTo] = useState<NursingTab | undefined>(undefined);
  /** ⭐ board 2812 M3: Tomorrow's waiting row in Bookings opens Nursing homes' To do
   *  with that request's card open (the same one-navigation hand-off as above). */
  const [nursingOpenRequest, setNursingOpenRequest] = useState<number | undefined>(undefined);
  /** ⭐ board 2812 M4 fix round (L5): how many times the rail has opened Nursing homes; the
   *  screen is keyed on it, so the rail always lands on All homes' To do. */
  const [nursingEntry, setNursingEntry] = useState(0);
  /**
   * ⭐ Board 2814 M8R1 (R3) - ONE TICK PER LANDED PORTAL-COUNT POLL, handed to the
   * Requests screen so its ROWS reload on the same 30-second poll as the COUNT (they
   * reloaded only on mount, so the badge could say 2 over a list of 1). No second
   * timer: the poll below bumps this after each read it lands.
   */
  const [portalPollTick, setPortalPollTick] = useState(0);
  /**
   * ⭐⭐ T1749 M8 (DS-20 d) — WHICH FILTER `Bookings` SHOULD OPEN ON, when
   * something else on the tool has asked. Done's `Back to Bookings` asks for the
   * pending work.
   *
   * The SAME hand-off shape as `nursingOpenOn` directly above, for the same two
   * reasons: it is not a ScreenId (Bookings already is one, and a filtered view
   * of a screen is not a second destination), and it is not in the STORE
   * (`navigate` moves between screens; which filter a screen opens on survives
   * exactly one navigation, so a store field would be a durable record of a
   * momentary request). ⚠️ CLEARED BY THE SCREEN, not by a timer: `Bookings`
   * reads it once at mount and calls `onOpened`.
   */
  const [bookingsOpenOn, setBookingsOpenOn] = useState<BookingsLanding | undefined>(undefined);
  /**
   * ⭐ board 2812 M5 (fix round 1, W2) - BACK FROM A BOOKING KEEPS THE LIST'S FILTER. Set
   * when Bookings (or a Nursing homes Bookings tab) opens a row, to the Who and When it
   * was found under; read once when that screen mounts again (Back pops to it) and
   * consumed there (`onOpened`), as `nursingReturnTo` is. Find is NOT kept: the box and
   * its filter start empty together (board 2812 M4's B1 rule). ⛔ Any other screen and
   * any rail press clear it, so the rail always opens on the ordinary default.
   */
  const [bookingsReturnAt, setBookingsReturnAt] = useState<BookingsLanding | undefined>(undefined);
  const [nursingBookingsWhen, setNursingBookingsWhen] = useState<BookingsWhen | undefined>(undefined);
  // HTMLElement, not HTMLDivElement: the pane is the <main> landmark now, and
  // preact types <main> as HTMLElement. Widened rather than cast, so the ref
  // keeps type-checking against whatever element the pane actually is.
  const paneRef = useRef<HTMLElement>(null);

  useEffect(() => store.subscribe(setState), [store]);
  useEffect(() => {
    if (state.screen !== 'booking-detail' && state.screen !== 'nursing-homes') {
      setNursingReturnTo(undefined);
      setNursingBookingsWhen(undefined);
    }
    if (state.screen !== 'booking-detail' && state.screen !== 'bookings') setBookingsReturnAt(undefined);
  }, [state.screen]);

  // Scroll reset + focus-to-title on every transition (§8). navSeq is the
  // store's transition counter — the render layer just follows it.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (paneRef.current) paneRef.current.scrollTop = 0;
    const title = document.querySelector<HTMLElement>('.screen-title');
    title?.focus();
  }, [state.navSeq]);

  // Badge freshness (§7.5): re-derive on every home navigation.
  useEffect(() => {
    if (state.screen === 'home' && state.operator !== null) {
      void refreshInboxCount(store);
    }
  }, [state.navSeq]);

  // ⭐ T807 -- Badge DISCOVERY. The effect above can only RE-DERIVE the badge on a
  // navigation, and the badge's three other refresh sites all fire after the operator
  // has acted on an inquiry she is already looking at. So the badge had no way to learn
  // about a lead that arrived while nobody navigated: a kiosk parked on Bookings -- the
  // natural place to leave it -- never found out an enquiry had come in. Paired with the
  // intake alert now being able to report its own failure, this is the half that
  // SURFACES the lead rather than merely recording that it was missed.
  //
  // ⛔ THE POLL IS NOT A NEW COST ON THE SCREEN THAT MATTERS -- check the baseline before
  // "optimising" it away. Bookings.tsx already refreshes every 15s (LIST_REFRESH_MS), so
  // the very screen in the defect report is making a request four times more often than
  // this tick does. Against the throttles it is free: authThrottled is consulted on
  // POST /api/login and NOWHERE else (server/app.ts, since T642 M5), and
  // GET /api/inquiries is auth-gated but not throttled -- so at 2 requests a minute this
  // consumes ZERO denying budget and cannot lock the operator out of her own kiosk.
  //
  // It still stops on 401/429, because the server guarantee must not depend on poller
  // discipline: an unattended tick against a dead session is exactly the hammer the auth
  // counters were written to distrust.
  useEffect(() => {
    if (state.operator === null) return undefined;
    const t = setInterval(() => {
      void refreshInboxCount(store).then((outcome) => {
        if (outcome === 'stop') clearInterval(t);
      });
    }, INBOX_POLL_MS);
    return () => clearInterval(t);
  }, [state.operator]);

  /**
   * ⭐ T928 slice 1 — the SAME discovery problem, for the other inbound queue.
   * Nursing-home requests arrive from outside on their own schedule, exactly
   * like web enquiries, and until this slice they had no badge at all: the only
   * way to learn one had come in was to go and look, three levels down inside
   * the price book. Promoting the surface to the rail without a count would fix
   * the finding and leave the noticing.
   *
   * Deliberately the same cadence and the same self-stopping shape as the tick
   * above rather than a new mechanism — including the `'stop'` outcome, which
   * matters MORE here: this poll crosses the kiosk -> portal HTTP seam, so an
   * unattended tick against a dead session is hitting a second service.
   *
   * ⛔ It runs on its OWN interval rather than being folded into the tick above.
   * A failure of either read must not stop the other: they are different seams
   * (local Postgres vs the portal over HTTP), and the portal one is the flakier.
   */
  useEffect(() => {
    if (state.operator === null) return undefined;
    // ⛔ THE IMMEDIATE READ IS NOT OPTIONAL HERE, unlike the inbox tick above.
    // That badge gets its first value from the home-navigation effect; this one
    // has no such hook, so without a read on mount it would render nothing at
    // all until the first 30s tick.
    let stopped = false;
    let t: ReturnType<typeof setInterval> | undefined;
    void refreshPortalCount(store).then((outcome) => {
      // ⛔ The immediate read HONOURS its own 'stop'. If the session is already
      // dead at mount, starting the interval anyway would spend a whole cycle
      // hitting a second service before noticing — the exact posture the auth
      // counters were written to distrust.
      if (outcome === 'stop') { stopped = true; return; }
      if (stopped) return;
      t = setInterval(() => {
        void refreshPortalCount(store).then((o) => {
          if (o === 'stop' && t !== undefined) clearInterval(t);
          // ⭐ Board 2814 M8R1 (R3): the rows follow a read that landed.
          if (o === 'ok') setPortalPollTick((n) => n + 1);
        });
      }, INBOX_POLL_MS);
    });
    return () => {
      // Set BEFORE the guard above can run: unmounting between the immediate
      // read resolving and the interval being created must not leak one.
      stopped = true;
      if (t !== undefined) clearInterval(t);
    };
  }, [state.operator]);

  // Send UI truth (SEC-6/SEC-7): entering Done on a saved booking loads the
  // server-of-record recipient + the latest send-ledger state.
  useEffect(() => {
    if (state.screen === 'done' && state.job.bookingId !== null) {
      void refreshBookingDetail(store);
    }
  }, [state.screen, state.job.bookingId]);

  // Precedent (T373 M5): when the quote screen shows a journey with a place but
  // no grid price (off-gazetteer or estimate), load the last agreed price(s) to
  // that place. Best-effort and read-only — a first-ever place returns nothing
  // and the line simply doesn't render. Fetch orchestration lives here (App);
  // QuoteScreen just projects the result.
  const precedentKey = precedentPlace(state.job);
  useEffect(() => {
    if (state.screen === 'quote' && precedentKey) {
      // Guard against an out-of-order resolution landing a stale precedent on a
      // newer job/place: a late response from a superseded effect is ignored.
      let live = true;
      void loadPrecedent(precedentKey, state.job.bookingId).then((rows) => {
        if (live) setPrecedent(rows);
      });
      return () => { live = false; };
    }
    setPrecedent([]);
    return undefined;
  }, [state.screen, precedentKey, state.job.bookingId]);

  /* ⛔ board 2814 M6 (DD-8): THE ACCOUNT'S CAPTURED-PRICE READ FOR THE QUOTE SCREEN
     WAS HERE and went with the nursing tile. No nursing job reaches the calculator
     any more; the agreed price is shown on the phone-in form (PhoneInTrip.tsx)
     through the same `resolveCapturedPrice`. */

  // Deposit poll: while Done shows a sent-but-unpaid deposit, re-check every
  // few seconds so the paid chip appears without a manual refresh.
  //
  // It STOPS on an auth failure. A poll that cannot authenticate cannot succeed,
  // and at 15 requests a minute from the operator's own IP it would trip the auth
  // throttle and lock her out of her own kiosk — no attacker required.
  useEffect(() => {
    if (state.screen !== 'done' || !state.job.depositSent || state.job.paymentStatus === 'deposit_paid') {
      return;
    }
    const t = setInterval(() => {
      void refreshPayment(store).then((outcome) => {
        if (outcome === 'stop') clearInterval(t);
      });
    }, 4000);
    return () => clearInterval(t);
  }, [state.screen, state.job.depositSent, state.job.paymentStatus]);

  if (state.screen === 'login' || state.operator === null || registry === null) {
    return (
      <Login
        onLogin={async (username, password) => {
          const result = await doLogin(store, username, password);
          if (!result.ok) return result.message;
          setRegistry(result.registry);
          // ⭐ T2016 G4 -- THE REPAIR AT THE FIRST MOMENT A SESSION CERTAINLY
          // EXISTS. `startPushOnLaunch` runs in index.tsx before anyone has signed
          // in, so on a cold start its POST answers 401 and is swallowed by design;
          // until now the only repair was opening "Who can sign in", which Helen has
          // no reason to do. This is the `onLogin` CALLBACK PROP (invoked from
          // Login.tsx), not a render path, so it runs once per successful sign-in.
          // ⛔ IT CANNOT PROMPT: `syncOnScreenOpen` delegates to `startPushOnLaunch`,
          // which returns early unless `permission() === 'granted'` (push-register.ts,
          // "⛔ IT NEVER PROMPTS"). No permission dialog can appear during login.
          // ⛔ NOT in actions.ts's `doLogin`: push-register.ts imports FROM actions.ts,
          // so that site would be an import cycle. App.tsx has no such edge.
          // ⚠️ Placed AFTER `setRegistry`, and the line below is the `return` — a call
          // put after it would be dead code TypeScript accepts in silence.
          void syncOnScreenOpen();
          return null;
        }}
      />
    );
  }

  const onNavigate = (screen: ScreenId) => store.update((s) => navigate(s, screen));
  /**
   * ⭐ board 2812 M4 fix round (L5): the RAIL's own dispatch. Its "Nursing homes" row
   * opens All homes on To do, as the mockup's entry does: it clears the home
   * (`openNursingFromRail`) and the Back hand-off, and it re-keys the screen so a press
   * while already on a home's tab starts the area afresh. Every other row navigates.
   */
  const onRailNavigate = (screen: ScreenId) => {
    // ⭐ board 2812 M5 (fix round 1, W2): the rail is an entry, never a Back.
    setBookingsReturnAt(undefined);
    setNursingBookingsWhen(undefined);
    if (screen !== 'nursing-homes') {
      onNavigate(screen);
      return;
    }
    setNursingReturnTo(undefined);
    setNursingEntry((n) => n + 1);
    store.update(openNursingFromRail);
  };
  const onHome = () => store.update(goHome);
  const onBack = () => store.update(goBack);
  const onLogout = () => {
    setRegistry(null);
    void doLogout(store);
  };

  const job = state.job;
  const descriptor = job.service ? serviceById(registry, job.service) : null;
  const isManual = descriptor?.pricingMode === 'manual';
  const svcLabel = job.service ? serviceLabel(registry, job.service) : null;
  const addOnLabels = Object.fromEntries(addOns(registry).map((a) => [a.engineKey, a.label]));
  const summary = journeySummary(state, { serviceLabel: svcLabel, addOnLabels });
  /**
   * ⭐ T1749 M8 round 3 — THE SAME JOURNEY, SPLIT, FOR DONE ALONE. Quote and the
   * lead strip keep the composed sentence (`summary` above); Done takes the line
   * and the extras separately and renders the extras as a caption, because with
   * five requirements the sentence wraps and pushes that screen's gold primary
   * past the ~700 plane (measured 703.3). Both halves come from the function
   * `journeySummary` itself composes, so the two renderings cannot disagree.
   */
  const doneSummary = journeyLineWithoutExtras(state, { serviceLabel: svcLabel, addOnLabels });
  const doneExtras = journeyExtras(state, { addOnLabels });

  let screen;
  switch (state.screen) {
    case 'home':
      screen = (
        <Home
          registry={registry}
          onStartJob={(serviceId) => {
            // Manual services skip the location step (incumbent parity);
            // pricingMode comes from the registry, never a client list.
            // ⛔ board 2814 M6 (DD-8): no nursing branch. The tile is gone and so is
            // its capture screen; nursing work is booked from the Nursing homes area.
            const mode = serviceById(registry, serviceId)?.pricingMode ?? 'grid';
            const next = mode === 'manual' ? 'passengers' : 'location';
            store.update((s) => navigate(startJob(s, serviceId), next));
          }}
        />
      );
      break;
    case 'inbox':
      screen = (
        <Inbox
          registry={registry}
          load={() => loadInbox(store)}
          onQuoteThis={(inq) => quoteThisInquiry(store, registry, inq)}
          onSetStatus={async (id, status, bookingId) => {
            const ok = await setInquiryStatus(id, status, bookingId);
            void refreshInboxCount(store);
            return ok;
          }}
          /* ⭐ board 2812 M3: a To do card and a Bookings row open the booking
             through the same navigation the Bookings list uses... */
          onOpenBooking={(bookingId) => store.update((s) => openBookingDetail(s, bookingId))}
          /* ...and a send or a Not going ahead on a card moves the Web badge now. */
          onChanged={() => void refreshInboxCount(store)}
        />
      );
      break;
    case 'location':
      screen = (
        <LocationScreen
          registry={registry}
          service={job.service ?? ''}
          pricingMode={descriptor?.pricingMode === 'p2p' ? 'p2p' : 'grid'}
          pickupLabel={job.locationLabel}
          dropoffLabel={job.toLocationLabel}
          // The structured town (T384 extra.town) beats the composed pickup as
          // the search pre-type — the composed text keeps its display slots.
          pickupText={job.locationSearchSeed ?? job.pickupSearchText}
          dropoffText={job.dropoffSearchText}
          routingKey={job.routingKey}
          // Priced inputs → the paired writers in actions.ts (T372's rule). Its
          // "Change" link makes this screen re-enterable FROM the quote, and Back
          // returns without passing Continue, so an unpaired patch here would land
          // Helen back on the quote holding a NEW pickup beside the OLD pickup's
          // price. setPickup/setDropoff also reset T373's off-gazetteer marker.
          onPickLocation={(id, label) => setPickup(store, id, label)}
          onClearLocation={() => setPickup(store, null, null)}
          onPickDropoff={(id, label) => setDropoff(store, id, label)}
          onClearDropoff={() => setDropoff(store, null, null)}
          // T373 M4 — hand-quote an off-gazetteer end. Also a paired writer: the
          // place is a priced input, so it re-prices (→ manual, settling
          // quotePending). The true place goes in the label (→ booking_stops +
          // journey summary), locationId stays null, and the marker passes the
          // readiness gate. THIS is where the Avoca substitution is designed out.
          onEscapePickup={(place) => setPickupOffGazetteer(store, place)}
          onEscapeDropoff={(place) => setDropoffOffGazetteer(store, place)}
          // T906 S1 — the golf ends. A SEPARATE writer from the two above, and
          // that separation is the whole design: setPickupOffGazetteer writes
          // the place into locationLabel with a null id, which on golf overwrites
          // the composite rate label and destroys the price. setGolfEnd states
          // the end as its own fact and re-resolves the PAIR, so a listed pair
          // keeps its grid price and only a genuinely unlisted one goes manual.
          golfPickupPlace={job.golfPickupPlace}
          golfDestinationPlace={job.golfDestinationPlace}
          onSetGolfEnd={(end, place) =>
            setGolfEnd(store, end, place, registry.options.golfPairs ?? [])
          }
          // T2090 - the airport direction and terminal: read off the job's own
          // `pickupTerminal` fact, written by the one writer, and NOT a priced
          // input (no re-price; the writer moves capture between the ends only).
          airportEnd={airportEndOf(job)}
          onSetAirportEnd={(direction, terminal) => setAirportEnd(store, direction, terminal)}
          // T385 item 6 — the widened miss envelope: escape taken / search
          // abandoned logs unresolved demand evidence (best-effort POST to
          // the existing session-authed endpoint; resolution stays NULL).
          onMiss={(typed) => reportLocationMiss(typed, job.service)}
          onContinue={() => {
            // Consult the band-aware router on the forward edge too — a band
            // the customer already gave must not be re-asked. Skipping gates
            // on VALIDITY (bands(registry)), never truthiness, so an unknown
            // band still lands on Passengers. Pair with fetchQuote exactly as
            // every other edge into add-ons/quote does: a skip that lands on
            // an unpriced screen is the same bug wearing a different hat.
            let target = 'passengers' as ScreenId;
            store.update((s) => {
              target = furthestReadyScreen(s.job, descriptor?.pricingMode ?? 'grid', bands(registry));
              return navigate(s, target);
            });
            if (target === 'addons' || target === 'quote') void fetchQuote(store);
          }}
        />
      );
      break;
    case 'passengers':
      screen = (
        <Passengers
          registry={registry}
          band={job.band}
          // ⛔⛔ T1871 M5 — THE COUNT'S NAME IS BACK IN THIS FILE, AND THAT IS A
          // DECLARED RE-ENTRY, NOT A LEAK. test/shared/passengers.spec.ts
          // derives its carrier population from RAW source by co-occurrence of
          // the count token with a money token, and its own note says an App.tsx
          // use "re-enters the derived set and the derivation arm reds until it
          // is re-declared here". It is re-declared there, with this one use.
          // The screen needs the VALUE (P0-3's trigger is a disjunction: a count
          // above 16 OR the over-band band); it never gates on its presence.
          passengersExact={job.passengersExact}
          // Through the paired writer, like every other band write. It hand-rolled
          // patch + fetchQuote before, which was correct but left the pairing rule
          // with an exception — and deleting that one `fetchQuote` kept the whole
          // suite green. One writer, one answer to grep.
          // T1871 M5 — `true` is the OPERATOR PIN: whatever Helen presses here,
          // a priced tile or the over-band edge, is her own decision and from
          // now on a changed headcount must not silently re-derive it out from
          // under her. The over-band edge reuses this same handler on purpose,
          // so the band write and the advance stay welded in ONE place.
          onPick={(band) => {
            setBand(store, band, true);
            store.update((s) => navigate(s, isManual ? 'quote' : 'addons'));
          }}
        />
      );
      break;
    case 'addons':
      screen = (
        <Addons
          registry={registry}
          service={job.service ?? ''}
          job={job}
          options={job.options}
          totalCents={job.quote?.totalCents ?? null}
          estimated={job.quote?.status === 'estimated'}
          quotePending={quotePending(state)}
          onChange={(key, value) => setOption(store, key, value)}
          // T400 driver details: plain patches — never the paired quote writers
          // (not priced inputs; town keeps driving grid pricing).
          onSetEnd={(patch) => store.update((s) => patchJob(s, patch))}
          onSetStop={(index, patch) => setOperatorStop(store, index, patch)}
          onSeePrice={() => store.update((s) => navigate(s, 'quote'))}
        />
      );
      break;
    case 'quote':
      screen = (
        <QuoteScreen
          job={job}
          summary={summary}
          isManual={isManual}
          registry={registry}
          quotePending={quotePending(state)}
          precedent={precedent}
          // T387 M3: venue is a PRICED input now (the engine consults standing
          // venue rates), so it goes through the paired writer like band/options.
          onSetVenue={(venue) => setVenueAndQuote(store, venue)}
          // Options used to patch with NO re-price — safe only while Requirements
          // was manual-only (a hand-typed price owes nothing to the engine). It
          // renders on the priced path now, where add-ons genuinely move the
          // total, so both of these go through the paired writers.
          onSetOption={(key, value) => setOption(store, key, value)}
          // T1871 M5 — the Quote chips are the OTHER operator band act, so they
          // pin too. Same reasoning as the Passengers tiles above.
          onSetBand={(band) => setBand(store, band, true)}
          // ⛔ T1871 M5 (P0-4) — no longer the inline plain patch T651 M7 (R21)
          // put here. The exact count derives the band now, which makes it a
          // PRICED write, so it lives with the other paired writers in
          // actions.ts where that rule is enforceable. See setPassengersExact
          // there for the three guards, and the exact-count docblock in
          // store/state.ts for the amendment this reverses.
          // ⚠️ THE TOKEN IS DELIBERATELY NOT SPELLED IN THIS FILE ANY MORE.
          // test/shared/passengers.spec.ts derives its carrier population by
          // co-occurrence of the count token with a money token over RAW source,
          // comments included, and refuses an entry with zero executable uses as
          // stale. Naming it here would put this file back in that population
          // with nothing to declare.
          onSetPassengersExact={(value) => setPassengersExact(store, value)}
          // ⛔⛔ T1871 M7 (MERGE BLOCKER) — THE COMMIT EDGE, AND THE SCREEN IS
          // INERT WITHOUT IT. The prop above writes the count on every
          // keystroke and derives nothing; this one derives the band once the
          // value has settled. Deriving on input destroyed a hand price
          // through the one-digit prefix of every two-digit headcount
          // (measured twice; the account is on the two writers in actions.ts).
          // over-band-hand-price.spec.tsx grades THIS LINE, so dropping it is
          // a red rather than a silently re-opened defect.
          onCommitPassengersExact={() => commitPassengersExact(store)}
          // The manual flow never visits Location, so it gets no route there.
          onChangeLocation={
            isManual ? null : () => store.update((s) => navigate(s, 'location'))
          }
          onSetDriverEnd={(patch) => store.update((s) => patchJob(s, patch))}
          customerExtrasLabels={
            job.customerFlags
              ? Object.entries(job.customerFlags)
                  .filter(([, v]) => v === true)
                  .map(([k]) => addOns(registry).find((a) => a.engineKey === k)?.label ?? k)
              : []
          }
          onPersist={(opts) => persistCurrentQuote(store, opts)}
          onPark={(noteDraft) => parkCurrentQuote(store, { noteDraft })}
          onSetCustomer={(name, phone, email, phoneCountry) =>
            store.update((s) =>
              // `operatorEmail` and never `customerEmail` (T660 M5): this value
              // is Helen-typed and is the only email-shaped field allowed on a
              // request body. Writing customerEmail here would launder the
              // public form's address through the authed capture door.
              // Board 2089 M3: the chosen country rides the same patch as the
              // digits it is read under. The two are one fact for the booking
              // POST (`createBookingForJob` sends both or neither).
              patchJob(s, {
                customerName: name,
                customerPhone: phone,
                operatorEmail: email,
                customerPhoneCountry: phoneCountry,
              }),
            )
          }
          onSetWhen={(patch) => store.update((s) => patchJob(s, patch))}
          onPreviewAlert={(name, phone) => previewTeamMessage(store, name, phone)}
          onSendAlert={sendTeamAlert}
          onDone={() => store.update((s) => navigate(s, 'done'))}
        />
      );
      break;
    case 'done':
      screen = (
        <Done
          job={job}
          operator={state.operator}
          summary={doneSummary}
          extras={doneExtras}
          sendStatusLabel={(status) => sendStatusLabel(registry, status)}
          channelLabel={(c) => channelLabel(registry, c)}
          channelConfigured={(c) => isChannelConfigured(registry, c)}
          onSendQuote={(includeDeposit, channel) => sendQuoteToCustomer(store, includeDeposit, channel)}
          /* ⭐ T1749 M8 (DS-20 d) — the one way back, to the PENDING quotes. The
             hand-off is set BEFORE the navigation so the Bookings mount that the
             same update triggers reads it on its first render; the screen clears
             it on that mount, so the rail's own Bookings entry is unaffected. */
          onBackToBookings={() => {
            // ⭐ board 2812 M3 (plan step 7): Who Web, When From today, since the
            // status chips it opened retired with the filter rows.
            setBookingsOpenOn(BACK_TO_BOOKINGS);
            store.update((s) => navigate(s, 'bookings'));
          }}
          // T524 — the manual copy route. The GUARD lives here, not in Done:
          // `quotePending` is a store selector and Done holds no store. It reads
          // the state at CLICK time (inside the callback), never at render time,
          // so a re-price that starts after the screen paints is still caught.
          //
          // Note the hazard is narrower than onPreviewAlert's above. That one
          // composes from client state, so a mid-re-price preview can pair a new
          // band with the previous band's price. This composes server-side from
          // the persisted quote row, so it can only return a price that was
          // really quoted. The guard is here because Helen must not copy a total
          // that differs from the one on the screen in front of her.
          onCopyQuoteMessage={async () => {
            const s = store.getState();
            if (quotePending(s)) {
              return { ok: false, message: COPY_REPRICING_COPY };
            }
            if (s.job.bookingId === null) {
              return { ok: false, message: COPY_FAILED_COPY };
            }
            const copied = await copyQuoteMessage(s.job.bookingId);
            // ⭐ board 2812 M3 (§7.5, Q246): a copy of the quote message is a quote
            // SEND, so a saved, unsent quote leaves the Web badge now, not a tick later.
            void refreshInboxCount(store);
            return copied;
          }}
          /**
           * T660 M3 (P0-3) — the payment-link copy row.
           *
           * ⚠️ NO `quotePending` GUARD, and the asymmetry with the handler
           * directly above is deliberate, checked, and worth the paragraph.
           *
           * That guard exists because the composed MESSAGE carries a total, and
           * Helen must not paste a number that differs from the one on her
           * screen. This action carries no total at all: it returns a URL, the
           * URL is never rendered, and the deposit behind it is computed
           * server-side from the PERSISTED quote row - the same row `finish()`
           * wrote and the same one the screen's headline came from. `POST
           * /api/quote` (what `quotePending` tracks) does not write that row: it
           * is a pure price computation, so an in-flight re-price cannot move the
           * amount this route reads. A guard here would refuse a correct copy and
           * would need a fourth near-duplicate sentence to explain itself.
           *
           * What DOES protect the re-priced case is server-side and stronger: if
           * the recorded price has moved on since the link was minted, M1's route
           * refuses with a 409 rather than handing back a link for the old
           * amount, and the action renders COPY_LINK_STALE_COPY.
           */
          onCopyPaymentLink={async () => {
            const s = store.getState();
            if (s.job.bookingId === null) {
              return { ok: false, message: COPY_LINK_FAILED_COPY };
            }
            return copyPaymentLink(s.job.bookingId);
          }}
          onNewQuote={() => store.update((s) => goHome(resetForNewQuote(s)))}
        />
      );
      break;
    case 'bookings':
      screen = (
        <Bookings
          registry={registry}
          onOpen={(bookingId, at) => {
            setBookingsReturnAt(at);
            store.update((s) => openBookingDetail(s, bookingId));
          }}
          /* ⭐ T1749 M8 (DS-20 d), re-pointed at board 2812 M3: undefined on an
             ordinary visit, so the screen's own default (All, From today) stands;
             Web and From today when Done sent her here; ⭐ board 2812 M5 (W2) the Who and
             When a booking was opened under, when Back returns here. */
          initialFilter={bookingsOpenOn ?? bookingsReturnAt}
          onOpened={() => {
            setBookingsOpenOn(undefined);
            setBookingsReturnAt(undefined);
          }}
          onOpenWaiting={(requestId) => {
            setNursingOpenOn('todo');
            setNursingOpenRequest(requestId);
            // ⭐ board 2812 M4: the waiting request is on All homes' To do, whichever home
            // was showing before.
            store.update((s) => openHome(s, null));
            onNavigate('nursing-homes');
          }}
        />
      );
      break;
    case 'booking-detail':
      screen =
        state.viewBookingId !== null ? (
          <BookingDetail
            registry={registry}
            bookingId={state.viewBookingId}
            onReopen={(detail) => void reopenBooking(store, registry, detail)}
            // ⭐ Board 2814 M2 fix round: an ask answered on the booking moves the
            // rail's count, through the same read the Requests screen triggers.
            onRequestsChanged={() => void refreshPortalCount(store)}
            onInboxChanged={() => void refreshInboxCount(store)}
          />
        ) : (
          <Bookings
            registry={registry}
            onOpen={(bookingId) => store.update((s) => openBookingDetail(s, bookingId))}
          />
        );
      break;
    case 'admin':
      /* ⛔ THE OPENER IS PASSED ONLY WHEN THE BUILD CARRIES THE CATALOG, and this
         line is the whole mechanism: `CATALOG_PRESENT` comes from the module
         esbuild resolved, so an OFF build hands `Admin` nothing and the shelf
         renders no `Design components` control. A link onto a null screen is the
         blank-pane class `store/state.ts` warns about; it is also a control Helen
         would meet on a shipped build and learn nothing from (§1's Helen-test). */
      screen = (
        <Admin
          registry={registry}
          onOpenCatalog={CATALOG_PRESENT ? () => onNavigate('catalog') : undefined}
          /* ⭐ T1749 M7 (R5): the shelf's quiet entry is a LINK-THROUGH now. It
             asks for the Nursing homes screen and the tab the account book is on;
             App owns both halves, so the shelf never learns a ScreenId. */
          onOpenAccountBook={() => {
            // ⭐ board 2812 M4: the account book is Set up homes and places now.
            setNursingOpenOn('setup');
            onNavigate('nursing-homes');
          }}
        />
      );
      break;
    /**
     * T1749 M5 — the component catalog. ⛔ NO `default:` IN THIS SWITCH, which is
     * what makes a new ScreenId a COMPILE error here rather than a blank pane; the
     * arm is added with the ScreenId, in the same commit, and `shell.spec.tsx`
     * carries the reachability triple (click the quiet link, the store's screen is
     * `catalog`, the heading renders) because nothing else asserts that the rail,
     * the union and this switch agree.
     */
    case 'catalog':
      screen = <Catalog />;
      break;
    // T928 slice 1 — both were sub-views of <Admin>, promoted to rail
    // destinations. ⛔ `currentOperator` is threaded HERE because it left
    // <Admin> in the same edit: it is `state.operator`, the display name the
    // server returned from GET /api/me, and Operators marks the reader's own
    // row with it. Drop it and the roster silently stops saying "(you)" —
    // nothing else notices, which is why it moves in one commit, not two.
    case 'operators':
      screen = <Operators currentOperator={state.operator} />;
      break;
    // T939 M5 — the nursing control centre, which REPLACED the `portal-requests`
    // arm rather than joining it: the requests queue is now a section of this
    // screen (Alex's ruling, 2026-08-10).
    //
    // ⛔ `onRequestsChanged` is the badge's write-side refresh, and it is the
    // same contract `Inbox` gets from `onSetStatus` a few arms up. The 30s poll
    // alone is not enough: this screen is where the count CHANGES, so without
    // this the badge lags Helen's own action by up to a full tick.
    //
    // ⚠️ This switch has NO `default:` arm, so a ScreenId with no case here
    // renders the PREVIOUS screen's markup silently — which is why the rail
    // button, the ScreenId and this arm are pinned together in shell.spec.
    // ⭐ T1011 M8 — `waitingRequests` IS THE SAME VALUE THE RAIL BADGE RENDERS,
    // and threading it here rather than letting the screen count again is what
    // discharges the second of the three grounds the anti-tab ruling stood on.
    // The badge counts waiting requests and the requests queue is now behind a
    // tab, so the badge could tick for something off-screen; the tab button
    // carries this number, from this one source, so the two cannot drift.
    // ⛔ `null` ON AN ERRORED READ, never 0. `portalCountError` means the count is
    // UNKNOWN, and a 0 there would tell Helen nothing is waiting on the exact
    // screen she came to check — the "never a silent zero" rule all three badge
    // counters keep.
    case 'nursing-homes':
      screen = (
        <NursingHomes
          key={`nursing-${String(nursingEntry)}`}
          /* ⭐ T1749 M7 (R5): undefined on an ordinary visit, so the screen's own
             bootstrap answer stands; `homes` when the shelf sent her here. */
          openOn={nursingOpenOn ?? nursingReturnTo}
          /* ⭐ T1749 M7 wave-3: the shelf asks for the ACCOUNTS segment of the
             Homes tab, which is where its accounts live, so the link-through
             lands on a visible strip rather than at the top of the roster. */
          openSection={nursingOpenOn === 'setup' ? 'accounts' : undefined}
          openRequestId={nursingOpenRequest}
          /* ⭐ board 2812 M5 (fix round 1, W2): the Bookings tab's When at the booking's
             opening, read once at mount like `openOn`. */
          bookingsWhen={nursingBookingsWhen}
          onOpened={() => {
            setNursingOpenOn(undefined);
            setNursingOpenRequest(undefined);
            setNursingReturnTo(undefined);
            setNursingBookingsWhen(undefined);
          }}
          onRequestsChanged={() => void refreshPortalCount(store)}
          waitingRequests={state.portalCountError ? null : state.portalCount}
          portalPollTick={portalPollTick}
          // ⭐ T1746 M1 (B4, Q108 option b) - the SAME navigation the Bookings
          // list uses, so a crossed request opens its booking exactly where
          // Helen would have found it anyway. It is a navigation and nothing
          // else: the second cancel is one click away, and it is still HERS.
          onOpenBooking={(bookingId, from, at) => {
            // ⭐ board 2812 M3 fix round (item 5): Back returns to the tab the booking was
            // opened on; ⭐ board 2812 M4 (step 0 (g)): every tab, and the home's page with
            // it (the store keeps `viewHomeKey` across the booking); ⭐ board 2812 M5 (W2)
            // and a Bookings tab's When.
            setNursingReturnTo(from);
            setNursingBookingsWhen(at?.when);
            store.update((s) => openBookingDetail(s, bookingId));
          }}
          /* ⭐ board 2812 M4 (plan step 2): which home's page shows, from the store. */
          viewHomeKey={state.viewHomeKey}
          onOpenHome={(key) => store.update((s) => openHome(s, key))}
          /* ⭐ board 2812 fix round (item 8): a home's Details, from the store, so Back
             closes it and stays on the home (`goBack`). */
          homeDetailsOpen={state.viewHomeDetails}
          onHomeDetails={(open) => store.update((s) => setHomeDetails(s, open))}
          registry={registry}
        />
      );
      break;
    case 'rate-changes':
      screen = (
        <RateChanges registry={registry} onResolved={() => void refreshRateChangesCount(store)} />
      );
      break;
    case 'triage':
      screen = <Triage />;
      break;
  }

  // ONE Back control for the whole app (incumbent parity: every screen past
  // home could step back). It pops the store's REAL visit history, so it can
  // never walk a screen Helen never saw (R17 P2-1) and never dead-ends: an
  // empty stack goes home. Hidden on home (nothing to retrace).
  const canGoBack = state.screen !== 'home';

  return (
    <div class="shell">
      {/* T556 - ONE render site for the feedback widget, mounted outside the
          screen switch so a recording survives every navigation. It is a
          position:fixed overlay, so it is not a flex item of .shell. Absent
          from the login branch above by construction, which is correct: no
          capture affordance before the passcode. In a default build this
          resolves to a stub that renders null. */}
      <FeedbackHost store={store} />
      <Rail state={state} onNavigate={onRailNavigate} onHome={onHome} onLogout={onLogout} />
      {/* T660 M6 P1-4a: the ONE main landmark. Every screen (all 18 .screen-pane
          sites) mounts inside it, so "skip to the work" is a landmark jump
          rather than a tab crawl through the rail. <main> is display:block like
          the <div> it replaces and .content is a class selector, so the layout
          is byte-identical. */}
      <main class="content" ref={paneRef}>
        {canGoBack && (
          <div class="content-topbar">
            <button type="button" class="btn btn--quiet" onClick={onBack}>Back</button>
          </div>
        )}
        {/* T486 — ONE render site for the lead strip, not one per screen. The
            wizard steps all pass through here, so this single mount is what
            un-blinds the golf entry too (golf is a variant inside Location,
            and a golf lead always routes to Location since its pickup never
            resolves). Quote is deliberately absent: CustomerInfoBlock is
            already there, and a second lead surface would duplicate it. */}
        {LEAD_CONTEXT_SCREENS.has(state.screen) && (
          <LeadContext job={job} serviceLabel={svcLabel} summary={summary} />
        )}
        {screen}
      </main>
    </div>
  );
}
